import { Injectable, Injector } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { FileToDownloadProductionWithheldFileRequestResourceMapper } from "../models/mappers/file-to-download-production-withheld-file-request-resource-mapper.mapper";
import { FileTypeToFileTypeResourceMapper } from "../models/mappers/file-type-to-file-type-resource.mapper";
import { PublicFileResourceToFileMapper } from "../models/mappers/public-file-resource-to-file.mapper";
import { FileDownloadDetailsResource } from "../models/resource/file-download-details.resource";
import { PublicFileResource } from "../models/resource/public-file.resource";
import { FileRecord, FileType } from "../models/ui/file.model";
import { FileUtility } from "../utilities/file.utility";
import { SdkBase } from "../utilities/sdk-base/sdk.base";
import { ProductionJobToProductionJobViewMapper } from "@models/mappers/production-job-to-production-job-view.mapper";
import { ProductionJobView } from "@models/ui/production-job-view.model";
import { ProductionJob } from "@models/review/production-job.model";
import { ODataRequest } from "../utilities/sdk-base/o-data-request";
import { ProductionJobViewToProductionJobMapper } from "@models/mappers/production-job-view-to-production-job.mapper";
import { BulkDocAddPayload } from "@models/review/bulk-doc-add-payload.model";
import { FieldLayoutViewModel } from "@models/field/field-layout-view-model.model";
import { ExportResponseResource } from "@models/resource/export-response.resource";
import { AuditResource } from "@models/resource/audit.resource";
import { ProductionJobAudit } from "@models/ui/production-job-audit.model";
import { AuditResourceToProductionJobAuditMapper } from "@models/mappers/audit-resource-to-production-job-audit.mapper";

const publicFileResourceToFileMapper = new PublicFileResourceToFileMapper();
const productionJobToProductionJobViewMapper = new ProductionJobToProductionJobViewMapper();
const productionJobViewToProductionJobMapper = new ProductionJobViewToProductionJobMapper();
@Injectable({
	providedIn: 'root'
})
export class ProductionJobSdk extends SdkBase {
	constructor(injector: Injector, private fileUtility: FileUtility) {
		super(injector);
	}
	public get(): Observable<ProductionJobView[]>
	public get(params?: {status: number}): Observable<ProductionJobView[]>
	public get(params?: {id: string}): Observable<ProductionJobView>
	public get(params?: GetJobParams): Observable<ProductionJobView | ProductionJobView[]>
	{
		if (params?.id) {
			const request = new ODataRequest('get', `productionJobs/${params?.id}`);
			return this.makeODataRequest$<ProductionJob>(request, 'api2').pipe(
				map(response => productionJobToProductionJobViewMapper.map(response))
			);
		}

		const request = new ODataRequest('get', 'productionJobs');
		if(params?.status != null || params?.status != undefined) {
			request.requestParams = request.requestParams.set('status', params.status);
		}

		return this.makeODataRequest$<ProductionJob[]>(request, 'api2').pipe(
			map(response => {
				const jobs: ProductionJobView[] = productionJobToProductionJobViewMapper.mapCollection(response);
				return jobs;
			})
		);
	}

	public createProduction(productionJobView: ProductionJobView): Observable<ProductionJobView> {
		const productionJob = productionJobViewToProductionJobMapper.map(productionJobView);
		return this.makeHttpRequest<ProductionJob>('post', `api2/productionJobs`, productionJob).pipe(
			map(job => productionJobToProductionJobViewMapper.map(job))
		);
	}

	public updateProductionJob(productionJobView: ProductionJobView): Observable<ProductionJobView> {
		const productionJob = productionJobViewToProductionJobMapper.map(productionJobView);
		const request = new ODataRequest('put','productionJobs', productionJob.productionJobId ,productionJob);
		return this.makeODataRequest$<ProductionJob>(request, 'api2');
	}

	public patch(productionJobId: string, productionJobDelta: Partial<ProductionJob>): Observable<any> {
		const body = [];
		for (const key in productionJobDelta) {
			body.push({
				op: "replace",
				path: `/${key}`,
				value: productionJobDelta[key]
			})
		}
		const request = new ODataRequest('patch','productionJobs', productionJobId ,body);
		return this.makeODataRequest$<ProductionJob>(request, 'api2');
	}

	public deleteProductionJob(productionJobId: string): Observable<boolean> {
		return this.makeHttpRequest<boolean>('delete', `api2/productionJobs/${productionJobId}`);
	}

	public getDownloadWithheldLink(productionJobId: string, title: string): Observable<any> {
		const url = `${this.getApiRoot()}/api2/production-downloads/withheld/${productionJobId}`;
		return this.httpClient.get<Blob>(url, { responseType: 'blob' as 'json' })
			.pipe(
				map((data) => {
					this.fileUtility.saveAsAnything(data, 'application/zip', title + '-privilege.zip');
				})
			);
	}

	public getWithheldFileList(params?: GetFileListParams): Observable<FileRecord[]> {
		if (!params?.id)
			return of(null);

		let url = `api2/withheld-production-jobs/${params?.id}/files`;
		if (params?.type) {
			const mapper = new FileTypeToFileTypeResourceMapper();
			url += `?fileType=${mapper.map(params?.type)}`;
		}

		return this.makeHttpRequest<GetProductionWithheldFilesResponseResource>('get', url).pipe(
			map((response) => {
				return publicFileResourceToFileMapper.mapCollection(response.files);
			})
		);
	}

	public downloadWithheld(params: ProductionDownloadParams): Observable<string> {
		if (params?.file) {
			const mapper = new FileToDownloadProductionWithheldFileRequestResourceMapper();
			return this.makeHttpRequest<FileDownloadDetailsResource>('post', `api2/withheld-production-jobs/download`, mapper.map(params.file)).pipe(map((response) => response.url));
		}
		return of(null);
	}

	public deleteWithheldFile(productionJobId: string, fileId: string): Observable<void> {
		return this.makeHttpRequest('delete', `api2/withheld-production-jobs/${productionJobId}?fileId=${fileId}`);
	}

	public forceCreateZip(productionJobId: string): Observable<void> {
		return this.makeHttpRequest('post', `api2/production-job/reset/${productionJobId}`, null);
	}

	public addDocsToProductionJob(productionJobId: string, payload: BulkDocAddPayload): Observable<any> {
		return this.makeHttpRequest('put',`api2/productionJobs/${productionJobId}/bulk`, payload);
	}

	public getLoadfileFieldLayout(productionJobId: string): Observable<FieldLayoutViewModel> {
		return this.makeHttpRequest<FieldLayoutViewModel>('get',`api2/productionJobs/${productionJobId}/loadFileFieldLayout`);
	}

	public getLoadfileFieldLayoutIsCustom(productionJobId: string): Observable<FieldLayoutViewModel> {
		return this.makeHttpRequest<FieldLayoutViewModel>('get',`api2/productionJobs/${productionJobId}/loadFileFieldLayoutIsCustom`);
	}

	public exportAllProductionJobs(): Observable<string> {
		return this.makeHttpRequest<ExportResponseResource>('post', 'api2/export/productionJobs', {}).pipe(
			map(((response) => response.downloadUrl))
		);
	}

	public getProductionJobAuditLog(productionJobId: string): Observable<ProductionJobAudit[]> {
		const mapper = new AuditResourceToProductionJobAuditMapper();
		return this.makeHttpRequest<AuditResource[]>('get', `api2/productionJobs/audit/${productionJobId}`).pipe(
			map((resource) => mapper.mapCollection(resource))
		);
	}
}

interface GetFileListParams {
	id: string;
	type?: FileType;
}

interface ProductionDownloadParams {
	file?: FileRecord;
	type?: FileType;
	productionJobId?: string;
}

export interface GetProductionWithheldFilesResponseResource {
	files: PublicFileResource[];
}

interface GetJobParams {
	id?: string;
	status?: number;
}
